import { Grid, Typography, Box } from '@mui/material';
import { ThemePalette } from 'mui.theme';
import { mapVehiclesAssets } from 'utils/helpers/mapAssetImage';
import { SVG } from 'components/Asset/SVG';
import type { productionAsset } from 'types/production.types';
import { convertToTwoDecimalPlaces } from 'components/Charts/Tooltip';
import { convertSeconds } from '../../utils';
import { type Asset } from 'types/asset.types';
import { useAppSelector } from 'store/hook';
import { IgnitionStatusIndicator } from 'components/StatusIndicator';
import useAppTheme from 'hooks/useAppTheme';
import { addLiveDataToAssets } from 'views/FleetOverview/helpers/asset.helpers';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  data: productionAsset;
}

const AssetItemDetails = ({ data }: Props) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { latestTagData } = useAppSelector((state) => state.deviceReducer);
  const { assets } = useAppSelector((state) => state.assetReducer);
  const assetsWithLiveData = addLiveDataToAssets(assets, latestTagData);
  return (
    <Box
      sx={{
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <SVG
        name={mapVehiclesAssets(data?.asset_type)}
        style={{
          fill: '#E2C171',
          height: '30px',
          width: '60px',
          paddingRight: '10px',
        }}
      />
      <Box sx={{ flex: 1 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={3}>
            <Typography
              sx={{
                color: palette.textSecondary,
                fontSize: '13px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'left',
                }}
              >
                <IgnitionStatusIndicator
                  latestTagData={latestTagData}
                  asset={
                    assetsWithLiveData.find(
                      (asset) => asset.bumperNumber === data.bumper_id
                    ) as Asset
                  }
                  size={10}
                  marginRight="5px"
                />
                {data?.bumper_id}
              </Box>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* <PowerSettingsNewIcon
                sx={{
                  color: data.device === 'aa' ? '#00C853' : '#E63946',
                  marginRight: '8px',
                  fontSize: '12px',
                }}
              /> */}
              <Typography
                sx={{
                  fontSize: '10px',
                  color: palette.textPrimary,
                }}
              >
                {data?.make} - {data?.model}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={2}>
            <Typography
              sx={{
                color: palette.textSecondary,
                fontSize: '11px',
              }}
            >
              {t('production')}
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                color: palette.textPrimary,
              }}
            >
              {convertToTwoDecimalPlaces(data?.max_load_sum?.toString())}{' '}
              {t('tons_unit')}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography
              sx={{
                color: palette.textSecondary,
                fontSize: '11px',
              }}
            >
              {t('loads_day')}
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                color: palette.textPrimary,
              }}
            >
              {convertToTwoDecimalPlaces(
                data?.avg_max_load_per_day?.toString()
              )}{' '}
              {t('tons_unit')}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography
              sx={{
                color: palette.textSecondary,
                fontSize: '11px',
              }}
            >
              {t('pass_count_cycle')}
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                color: palette.textPrimary,
              }}
            >
              {convertToTwoDecimalPlaces(data?.pass_count_cycle?.toString())}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography
              sx={{
                color: palette.textSecondary,
                fontSize: '11px',
              }}
            >
              {t('hauler_cycle_time')}
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                color: palette.textPrimary,
              }}
            >
              {convertToTwoDecimalPlaces(
                convertSeconds(data?.avg_cycle_time).value.toString()
              )}{' '}
              {convertSeconds(data?.avg_cycle_time).unit}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default AssetItemDetails;
